import * as React from 'react';

import type * as api from '@gitbook/api-client';

import {
    type ColorPickerOption,
    colorPickerDefaultBold,
    colorPickerDefaultLight,
} from '../common/colorPickerOptions';

export interface ContentEditorColorGroup {
    key: string;
    title: string;
    kind: 'text' | 'background';
    colors: ColorPickerOption[];
}

export interface ContentEditorColorContextValue {
    groups: ContentEditorColorGroup[];
}

export const DEFAULT_EDITOR_TEXT_COLORS = colorPickerDefaultBold;
export const DEFAULT_EDITOR_BACKGROUND_COLORS = colorPickerDefaultLight;

export const DEFAULT_EDITOR_COLOR_GROUPS: ContentEditorColorGroup[] = [
    {
        key: 'text',
        title: 'Text',
        kind: 'text',
        colors: colorPickerDefaultBold,
    },
    {
        key: 'background',
        title: 'Background',
        kind: 'background',
        colors: colorPickerDefaultLight,
    },
];

export const ContentEditorColorContext = React.createContext<ContentEditorColorContextValue>({
    groups: DEFAULT_EDITOR_COLOR_GROUPS,
});

/**
 * Hook to get a color in the content editor.
 */
export function useContentEditorColor(
    props:
        | {
              type: 'text';
              color: api.DocumentTextColor | null;
          }
        | {
              type: 'background';
              color: api.DocumentTextBackgroundColor | null;
          }
) {
    const { type, color } = props;
    const { groups } = React.useContext(ContentEditorColorContext);
    return React.useMemo(
        () => (color && color !== 'default' ? findColorInGroups(groups, color, type) : null),
        [color, groups, type]
    );
}

/**
 * Find a color in the defined color groups.
 */
export function findColorInGroups(
    groups: ContentEditorColorGroup[],
    colorKey: string,
    kind: 'text' | 'background'
): ColorPickerOption | null {
    for (const group of groups) {
        if (group.kind === kind) {
            const color = group.colors.find((c) => c.key === colorKey);
            if (color) {
                return color;
            }
        }
    }
    return null;
}
